import { useState, type ChangeEvent, type FormEvent, type JSX } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {supabase} from "../clients/supabaseClient.tsx";

interface LoginFormData { email: string; password: string; }
interface LocationState { from?: { pathname: string } }

const Login = (): JSX.Element => {
    const navigate = useNavigate();
    const location = useLocation();
    const { signIn } = useAuth();

    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [formData, setFormData] = useState<LoginFormData>({ email: '', password: '' });
    const [showForgotPassword, setShowForgotPassword] = useState(false);
    const [resetEmail, setResetEmail] = useState('');
    const [resetSent, setResetSent] = useState(false);
    const [resetLoading, setResetLoading] = useState(false);

    const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;             // ✅ must be "email" / "password"
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setIsLoading(true);
        setError(null);

        try {
            // ✅ ensure we pass exactly (email, password)
            await signIn(formData.email.trim(), formData.password);
            const state = location.state as LocationState;
            navigate(state?.from?.pathname ?? '/deliveries', { replace: true });
        } catch (err) {
            const msg = err instanceof Error ? err.message : 'Sign-in failed';
            setError(msg);
        } finally {
            setIsLoading(false);
        }
    };

    const handleForgotPassword = async (e: React.FormEvent) => {
        e.preventDefault();
        setResetLoading(true);
        const { error } = await supabase.auth.resetPasswordForEmail(resetEmail, {
            redirectTo: 'https://save-staging.fxtech.solutions/reset-password',
        });
        setResetLoading(false);
        if (!error) setResetSent(true);
    };

    return (
        <div className="min-h-screen bg-stone-50 flex items-center justify-center px-4 py-12">
            <div className="w-full max-w-md">
                <div className="text-center mb-6">
                    <h1 className="font-display text-2xl tracking-tight text-ink-900">Save Delivery</h1>
                    <p className="text-sm text-gray-600 mt-1">Sign in to continue</p>
                </div>

                <div className="rounded-2xl border border-stone-200 bg-white shadow-card p-6">
                    {error && (
                        <div role="alert" className="mb-4 rounded-xl border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800">
                            {error}
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-5">
                        <div>
                            <label htmlFor="email" className="block text-sm text-gray-700 mb-1">Email</label>
                            <input
                                id="email" name="email" type="email" autoComplete="email" required
                                value={formData.email} onChange={handleInputChange}
                                className="w-full h-11 rounded-xl border border-stone-300 px-3 text-ink-900 placeholder-gray-400
                           focus:outline-none focus:ring-0 focus:border-ink-900"
                                placeholder="you@example.com"
                            />
                        </div>

                        <div>
                            <label htmlFor="password" className="block text-sm text-gray-700 mb-1">Password</label>
                            <input
                                id="password" name="password" type="password" autoComplete="current-password" required
                                value={formData.password} onChange={handleInputChange}
                                className="w-full h-11 rounded-xl border border-stone-300 px-3 text-ink-900 placeholder-gray-400
                           focus:outline-none focus:ring-0 focus:border-ink-900"
                                placeholder="••••••••"
                            />
                        </div>

                        <div className="flex justify-end">
                            <button
                                type="button"
                                onClick={() => setShowForgotPassword(true)}
                                className="text-sm text-stone-500 hover:text-stone-700 underline"
                            >
                                Forgot your password?
                            </button>
                        </div>

                        {/* ✅ black, visible, and actually submits */}
                        <button
                            type="submit"
                            aria-busy={isLoading}
                            disabled={isLoading}
                            className="w-full h-12 rounded-xl bg-[#e6186c] py-2 text-white hover:bg-[#d01560]  font-semibold
                         transition disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                            {isLoading ? 'Signing in…' : 'Sign in'}
                        </button>
                    </form>
                </div>
                {showForgotPassword && (
                    <div className="mt-4 rounded-lg border border-stone-200 bg-stone-50 p-4">
                        {resetSent ? (
                            <div className="text-center">
                                <p className="text-sm font-medium text-stone-800">Check your email</p>
                                <p className="mt-1 text-xs text-stone-500">
                                    If an account exists for {resetEmail}, a password reset link has been sent.
                                </p>
                                <button
                                    onClick={() => { setShowForgotPassword(false); setResetSent(false); setResetEmail(''); }}
                                    className="mt-3 text-xs text-[#e6186c] hover:underline"
                                >
                                    Back to login
                                </button>
                            </div>
                        ) : (
                            <form onSubmit={handleForgotPassword} className="space-y-3">
                                <p className="text-sm font-medium text-stone-800">Reset your password</p>
                                <p className="text-xs text-stone-500">Enter your email and we'll send you a reset link.</p>
                                <input
                                    type="email"
                                    required
                                    value={resetEmail}
                                    onChange={e => setResetEmail(e.target.value)}
                                    placeholder="you@example.com"
                                    className="block w-full rounded-lg border border-stone-300 px-3 py-2 text-sm focus:border-[#e6186c] focus:outline-none focus:ring-2 focus:ring-[#e6186c]/20"
                                />
                                <div className="flex gap-2">
                                    <button
                                        type="button"
                                        onClick={() => setShowForgotPassword(false)}
                                        className="flex-1 rounded-lg border border-stone-300 py-2 text-sm text-stone-600 hover:bg-stone-100"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={resetLoading}
                                        className="flex-1 rounded-lg bg-[#e6186c] py-2 text-sm font-medium text-white hover:bg-[#d01560] disabled:opacity-60"
                                    >
                                        {resetLoading ? 'Sending...' : 'Send reset link'}
                                    </button>
                                </div>
                            </form>
                        )}
                    </div>
                )}

                <p className="mt-6 text-center text-xs text-gray-500">Protected area • Unauthorized access prohibited</p>
            </div>
        </div>
    );
};

export default Login;